import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, Heading, SimpleGrid, Text, Button, Flex, 
  useColorModeValue, Badge, Icon, Avatar, HStack, VStack,
  Tabs, TabList, TabPanels, Tab, TabPanel, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalBody, ModalCloseButton, useDisclosure,
  IconButton, Tooltip, useToast, Progress
} from '@chakra-ui/react';
import { MdVideoCameraFront, MdOutlineTimer, MdPlayCircle, MdDelete, MdCloudDone, MdAccessTime, MdFileDownload } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import VideoPlayer from './videoplayer';

export default function ActiveMeetings() {
  const [meetings, setMeetings] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({}); // { [recordingId]: percentage }
  const { isOpen: isVideoOpen, onOpen: onVideoOpen, onClose: onVideoClose } = useDisclosure();
  
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useContext(AuthContext);
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  
  const headingColor = useColorModeValue("gray.800", "white");
  const itemBorderColor = useColorModeValue("gray.200", "gray.700");
  const itemBgColor = useColorModeValue("white", "gray.900");
  const instructorBg = useColorModeValue("gray.50", "gray.800");

  const fetchMeetings = async () => {
    try {
      const response = await axiosInstance.get(`/meetings/active`);
      if (response.data.success) {
        setMeetings(response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch active meetings", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecordings = async () => {
    setLoadingRecordings(true);
    try {
      const response = await axiosInstance.get(`/meetings/recordings`);
      if (response.data.success) {
        setRecordings(response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch recordings", err);
    } finally {
      setLoadingRecordings(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
    fetchRecordings();
    const interval = setInterval(() => {
      fetchMeetings();
      fetchRecordings();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleJoin = (roomName) => {
    navigate(`/meet/${roomName}`);
  };

  const handleEnd = async (roomName) => {
    try {
      await axiosInstance.post(`/meetings/end/${encodeURIComponent(roomName)}`);
      fetchMeetings();
      fetchRecordings();
      toast({ title: "تم إنهاء الحصة", status: "info", duration: 2000 });
    } catch (err) {
      console.error("Failed to end meeting", err);
    }
  };

  const handlePlayRecording = (rec) => {
    setSelectedRecording(rec);
    onVideoOpen();
  };

  const handleDeleteRecording = async (recId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا التسجيل السحابي نهائياً؟")) return;
    try {
      const res = await axiosInstance.delete(`/meetings/recording/${recId}`);
      if (res.data.success) {
        toast({ title: "تم حذف التسجيل بنجاح", status: "success", duration: 3000 });
        fetchRecordings();
      }
    } catch (err) {
      toast({ title: "تعذر حذف التسجيل", description: err.response?.data?.error || err.message, status: "error" });
    }
  };

  const handleDownloadRecording = async (rec) => {
    if (!rec || !rec._id) {
      toast({
        title: "لا يوجد ملف تسجيل متاح",
        status: "warning",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setDownloadProgress(prev => ({ ...prev, [rec._id]: 1 }));
    toast({
      title: "جاري فتح التسجيل...",
      status: "info",
      duration: 2500,
      isClosable: true
    });

    // Pre-open tab synchronously on user click to guarantee popup blockers do not block it
    const newTab = window.open('about:blank', '_blank');

    try {
      // Step 1: Get a signed JWT token
      const { data } = await axiosInstance.post(`/meetings/recording/download-token/${rec._id}`);

      if (!data.success || !data.token) {
        throw new Error(data.error || 'فشل الحصول على رابط التشغيل');
      }

      // Step 2: Open the video directly in the tab.
      // The backend serves it with Content-Disposition: inline + Content-Type: video/mp4 + Range support
      // so the browser opens its native player with full seeking and built-in download.
      const baseURL = axiosInstance.defaults.baseURL;
      const videoUrl = `${baseURL}/meetings/recording/download/${rec._id}?token=${data.token}`;

      if (newTab && !newTab.closed) {
        newTab.opener = null;
        newTab.location.href = videoUrl;
      } else {
        window.open(videoUrl, '_blank', 'noopener,noreferrer');
      }

      setDownloadProgress(prev => ({ ...prev, [rec._id]: 100 }));
      toast({
        title: "تم فتح التسجيل في تبويب جديد",
        description: "يمكنك مشاهدة الفيديو وتحميله من خلال المشغل",
        status: "success",
        duration: 4000,
        isClosable: true
      });
    } catch (err) {
      if (newTab && !newTab.closed) {
        newTab.close();
      }
      console.error("Open recording error:", err);
      toast({
        title: "فشل فتح التسجيل",
        description: err.response?.data?.error || err.message || "تعذر فتح ملف التسجيل",
        status: "error",
        duration: 4000,
        isClosable: true
      });
    } finally {
      setTimeout(() => {
        setDownloadProgress(prev => {
          const next = { ...prev };
          delete next[rec._id];
          return next;
        });
      }, 2000);
    }
  };


  const formatSeconds = (sec) => {
    if (!sec || sec <= 0) return "—";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    if (m >= 60) {
      const h = Math.floor(m / 60);
      const remM = m % 60;
      return `${h} س و ${remM} د`;
    }
    return `${m} د و ${s} ث`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const mb = (bytes / (1024 * 1024)).toFixed(1);
    return `${mb} MB`;
  };

  return (
    <Box p={{ base: 4, md: 8 }} shadow="xl" borderWidth="1px" borderColor={borderColor} borderRadius="3xl" bg={bgColor} width="100%">
      <Tabs variant="soft-rounded" colorScheme="blue" dir="rtl">
        <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
          <TabList bg={instructorBg} p={1.5} borderRadius="2xl" border="1px solid" borderColor={itemBorderColor}>
            <Tab fontWeight="bold" fontSize="sm" borderRadius="xl">
              <HStack spacing={2}>
                <Icon as={MdVideoCameraFront} />
                <Text>الحصص المباشرة الآن</Text>
                {meetings.length > 0 && (
                  <Badge colorScheme="red" variant="solid" borderRadius="full" px={2}>
                    {meetings.length}
                  </Badge>
                )}
              </HStack>
            </Tab>
            <Tab fontWeight="bold" fontSize="sm" borderRadius="xl">
              <HStack spacing={2}>
                <Icon as={MdCloudDone} color="blue.500" />
                <Text>تسجيلات الحصص السحابية</Text>
                {recordings.length > 0 && (
                  <Badge colorScheme="blue" variant="solid" borderRadius="full" px={2}>
                    {recordings.length}
                  </Badge>
                )}
              </HStack>
            </Tab>
          </TabList>

          <Button size="sm" variant="ghost" onClick={() => { fetchMeetings(); fetchRecordings(); }}>
            🔄 تحديث
          </Button>
        </Flex>

        <TabPanels>
          {/* TAB 1: LIVE ACTIVE MEETINGS */}
          <TabPanel p={0}>
            {meetings.length === 0 ? (
              <Flex 
                direction="column" 
                align="center" 
                justify="center" 
                p={10} 
                bg={itemBgColor} 
                borderRadius="2xl" 
                borderWidth="1px" 
                borderColor={borderColor} 
                borderStyle="dashed"
              >
                <Box p={4} bg="blue.50" rounded="full" color="blue.600" mb={4}>
                  <Icon as={MdOutlineTimer} w={10} h={10} />
                </Box>
                <Heading size="md" color="gray.600" mb={2} dir="rtl">لا توجد حصص مباشرة نشطة حالياً</Heading>
                <Text color="gray.500" dir="rtl">الرجاء الانتظار حتى يقوم المدرس ببدء الحصة</Text>
              </Flex>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} dir="rtl">
                {meetings.map((meeting) => {
                  const isOwnerOrAdmin = user?.role === 'admin' || user?.role === 'instructor' || user?._id === meeting.createdBy?._id || user?.id === meeting.createdBy?._id;
                  
                  return (
                    <Flex 
                      key={meeting._id} 
                      direction="column"
                      justify="space-between"
                      p={6} 
                      borderWidth="1px" 
                      borderColor={itemBorderColor} 
                      borderRadius="2xl" 
                      bg={itemBgColor}
                      boxShadow="sm"
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                      position="relative"
                      overflow="hidden"
                      _hover={{ 
                        transform: "translateY(-6px)", 
                        boxShadow: "2xl",
                        borderColor: "blue.400"
                      }}
                    >
                      <Box>
                        {/* Top Badge Info Bar */}
                        <Flex justify="space-between" align="center" mb={4}>
                          <Badge colorScheme="red" variant="solid" px={3} py={1} borderRadius="full" fontSize="xs" display="flex" alignItems="center" gap={1.5}>
                            <Box w={2} h={2} bg="white" borderRadius="full" className="pulse-animation" />
                            مباشر الآن
                          </Badge>
                          <Badge colorScheme="blue" variant="subtle" px={2.5} py={1} borderRadius="lg" fontSize="xs" fontWeight="bold">
                            ID: {meeting.meetingId}
                          </Badge>
                        </Flex>

                        {/* Meeting Room Title */}
                        <Heading size="md" color={headingColor} mb={4} minH="50px" lineHeight="1.4" fontWeight="bold">
                          {meeting.roomName}
                        </Heading>

                        {/* Instructor Info Box */}
                        <Flex align="center" gap={3} p={3.5} bg={instructorBg} borderRadius="xl" border="1px solid" borderColor="blackAlpha.50">
                          <Avatar size="sm" name={meeting.createdBy?.username} bg="blue.500" color="white" />
                          <Box overflow="hidden">
                            <Text fontSize="11px" color="gray.500" fontWeight="medium">المدرس / المحاضر</Text>
                            <Text fontSize="sm" fontWeight="bold" color={headingColor} isTruncated>
                              {meeting.createdBy?.username || 'غير معروف'}
                            </Text>
                          </Box>
                        </Flex>
                      </Box>

                      {/* Action Buttons */}
                      <HStack spacing={3} mt={6} pt={4} borderTop="1px solid" borderColor="gray.100">
                        <Button 
                          colorScheme="blue" 
                          size="md" 
                          onClick={() => handleJoin(meeting.meetingId)}
                          borderRadius="xl"
                          flex={1}
                          fontWeight="bold"
                          boxShadow="0 4px 14px 0 rgba(49, 130, 206, 0.35)"
                          _hover={{ transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(49, 130, 206, 0.5)" }}
                        >
                          انضمام للحصة
                        </Button>
                        {isOwnerOrAdmin && (
                          <Button 
                            colorScheme="red" 
                            variant="outline"
                            size="md" 
                            onClick={() => handleEnd(meeting.roomName)}
                            borderRadius="xl"
                            px={4}
                            _hover={{ bg: "red.50" }}
                          >
                            إنهاء
                          </Button>
                        )}
                      </HStack>
                    </Flex>
                  );
                })}
              </SimpleGrid>
            )}
          </TabPanel>

          {/* TAB 2: CLOUD RECORDED MEETINGS */}
          <TabPanel p={0}>
            {recordings.length === 0 ? (
              <Flex 
                direction="column" 
                align="center" 
                justify="center" 
                p={10} 
                bg={itemBgColor} 
                borderRadius="2xl" 
                borderWidth="1px" 
                borderColor={borderColor} 
                borderStyle="dashed"
              >
                <Box p={4} bg="blue.50" rounded="full" color="blue.600" mb={4}>
                  <Icon as={MdCloudDone} w={10} h={10} />
                </Box>
                <Heading size="md" color="gray.600" mb={2} dir="rtl">لا توجد تسجيلات سحابية بعد</Heading>
                <Text color="gray.500" dir="rtl">عند تسجيل أي حصة مباشرة ستظهر هنا تلقائياً وتكون متاحة للمشاهدة في أي وقت.</Text>
              </Flex>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} dir="rtl">
                {recordings.map((rec) => {
                  const isOwnerOrAdmin = user?.role === 'admin' || user?._id === rec.createdBy?._id || user?.id === rec.createdBy?._id || user?._id === rec.createdBy || user?.id === rec.createdBy;
                  const dateStr = rec.createdAt ? new Date(rec.createdAt).toLocaleDateString('ar-TN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                  
                  return (
                    <Flex 
                      key={rec._id} 
                      direction="column"
                      justify="space-between"
                      p={6} 
                      borderWidth="1px" 
                      borderColor={itemBorderColor} 
                      borderRadius="2xl" 
                      bg={itemBgColor}
                      boxShadow="sm"
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                      position="relative"
                      overflow="hidden"
                      _hover={{ 
                        transform: "translateY(-6px)", 
                        boxShadow: "2xl",
                        borderColor: "blue.400"
                      }}
                    >
                      <Box>
                        {/* Top Badge Info Bar */}
                        <Flex justify="space-between" align="center" mb={4}>
                          <Badge colorScheme="blue" variant="solid" px={3} py={1} borderRadius="full" fontSize="xs" display="flex" alignItems="center" gap={1.5}>
                            <Icon as={MdCloudDone} />
                            تسجيل سحابي
                          </Badge>
                          <Text fontSize="xs" color="gray.500" fontWeight="600">
                            {dateStr}
                          </Text>
                        </Flex>

                        {/* Meeting Room Title */}
                        <Heading size="md" color={headingColor} mb={3} minH="45px" lineHeight="1.4" fontWeight="bold">
                          {rec.roomName}
                        </Heading>

                        {/* Details pills */}
                        <HStack spacing={2} mb={4} flexWrap="wrap">
                          {rec.recordingDuration > 0 && (
                            <Badge colorScheme="purple" variant="subtle" borderRadius="md" px={2} py={0.5} display="flex" alignItems="center" gap={1}>
                              <Icon as={MdAccessTime} />
                              {formatSeconds(rec.recordingDuration)}
                            </Badge>
                          )}
                          {rec.recordingSize > 0 && (
                            <Badge colorScheme="gray" variant="subtle" borderRadius="md" px={2} py={0.5}>
                              {formatFileSize(rec.recordingSize)}
                            </Badge>
                          )}
                          {rec.matiere && (
                            <Badge colorScheme="teal" variant="subtle" borderRadius="md" px={2} py={0.5}>
                              {rec.matiere}
                            </Badge>
                          )}
                        </HStack>

                        {/* Instructor Info Box */}
                        <Flex align="center" gap={3} p={3} bg={instructorBg} borderRadius="xl" border="1px solid" borderColor="blackAlpha.50">
                          <Avatar size="sm" name={rec.createdBy?.username} bg="blue.500" color="white" />
                          <Box overflow="hidden">
                            <Text fontSize="10px" color="gray.500">المدرس</Text>
                            <Text fontSize="xs" fontWeight="bold" color={headingColor} isTruncated>
                              {rec.createdBy?.username || 'غير معروف'}
                            </Text>
                          </Box>
                        </Flex>
                      </Box>

                      {/* Action Buttons */}
                      <Box mt={6} pt={4} borderTop="1px solid" borderColor="gray.100">
                        {downloadProgress[rec._id] !== undefined && (
                          <Box mb={3} p={2.5} bg="blue.50" _dark={{ bg: "blue.900" }} borderRadius="xl">
                            <Flex justify="space-between" align="center" mb={1.5}>
                              <Text fontSize="xs" fontWeight="bold" color="blue.600" _dark={{ color: "blue.200" }}>
                                {downloadProgress[rec._id] === 100 ? "اكتمل التحميل!" : "جاري التحميل..."}
                              </Text>
                              <Badge colorScheme="blue" borderRadius="full" px={2} fontSize="xs" fontWeight="bold">
                                {downloadProgress[rec._id]}%
                              </Badge>
                            </Flex>
                            <Progress 
                              value={downloadProgress[rec._id]} 
                              size="xs" 
                              colorScheme="blue" 
                              borderRadius="full" 
                              hasStripe 
                              isAnimated 
                            />
                          </Box>
                        )}
                        <HStack spacing={2}>
                          <Button 
                            leftIcon={<MdPlayCircle size={20} />}
                            colorScheme="green" 
                            size="md" 
                            onClick={() => handlePlayRecording(rec)}
                            borderRadius="xl"
                            flex={1}
                            fontWeight="bold"
                            boxShadow="0 4px 14px 0 rgba(72, 187, 120, 0.35)"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(72, 187, 120, 0.5)" }}
                          >
                            مشاهدة التسجيل
                          </Button>
                          <Tooltip label={downloadProgress[rec._id] !== undefined ? `جاري التحميل: ${downloadProgress[rec._id]}%` : "تحميل التسجيل"} hasArrow>
                            <Button 
                              leftIcon={<MdFileDownload size={18} />}
                              colorScheme="blue" 
                              variant={downloadProgress[rec._id] !== undefined ? "solid" : "outline"}
                              size="md" 
                              onClick={() => handleDownloadRecording(rec)}
                              isDisabled={downloadProgress[rec._id] !== undefined}
                              borderRadius="xl"
                              aria-label="تحميل التسجيل"
                              minW={downloadProgress[rec._id] !== undefined ? "85px" : "auto"}
                              px={3}
                              _hover={{ bg: "blue.50", transform: "translateY(-2px)" }}
                            >
                              {downloadProgress[rec._id] !== undefined ? `${downloadProgress[rec._id]}%` : "تحميل"}
                            </Button>
                          </Tooltip>
                          {isOwnerOrAdmin && (
                            <Tooltip label="حذف التسجيل" hasArrow>
                              <IconButton 
                                icon={<MdDelete />}
                                colorScheme="red" 
                                variant="ghost"
                                size="md" 
                                onClick={() => handleDeleteRecording(rec._id)}
                                borderRadius="xl"
                                aria-label="Delete recording"
                              />
                            </Tooltip>
                          )}
                        </HStack>
                      </Box>
                    </Flex>
                  );
                })}
              </SimpleGrid>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* VIDEO PLAYER MODAL FOR CLOUD RECORDINGS */}
      <Modal isOpen={isVideoOpen} onClose={onVideoClose} size="4xl" isCentered>
        <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.700" />
        <ModalContent dir="rtl" borderRadius="2xl" overflow="hidden" bg="gray.900" color="white">
          <ModalHeader py={4} px={6} borderBottom="1px solid rgba(255,255,255,0.1)">
            <Flex justify="space-between" align="center" w="100%" pl={10}>
              <HStack spacing={3}>
                <Icon as={MdCloudDone} color="blue.400" boxSize={6} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="lg" fontWeight="bold">{selectedRecording?.roomName || "تسجيل الحصة"}</Text>
                  <Text fontSize="xs" color="gray.400">
                    {selectedRecording?.createdAt ? new Date(selectedRecording.createdAt).toLocaleString('ar-TN') : ''}
                  </Text>
                </VStack>
              </HStack>
              {selectedRecording?.recordingUrl && (
                <HStack spacing={3}>
                  {downloadProgress[selectedRecording._id] !== undefined && (
                    <Box minW="110px">
                      <Flex justify="space-between" align="center" mb={1}>
                        <Text fontSize="10px" color="gray.300">التقدم</Text>
                        <Text fontSize="xs" fontWeight="bold" color="blue.300">
                          {downloadProgress[selectedRecording._id]}%
                        </Text>
                      </Flex>
                      <Progress 
                        value={downloadProgress[selectedRecording._id]} 
                        size="xs" 
                        colorScheme="blue" 
                        borderRadius="full" 
                        hasStripe 
                        isAnimated 
                      />
                    </Box>
                  )}
                  <Button
                    leftIcon={<MdFileDownload size={18} />}
                    colorScheme="blue"
                    size="sm"
                    borderRadius="xl"
                    onClick={() => handleDownloadRecording(selectedRecording)}
                    isDisabled={downloadProgress[selectedRecording._id] !== undefined}
                    _hover={{ bg: "blue.400" }}
                  >
                    {downloadProgress[selectedRecording._id] !== undefined 
                      ? `${downloadProgress[selectedRecording._id]}%` 
                      : "تحميل التسجيل"}
                  </Button>
                </HStack>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" left={4} right="unset" top={4} />
          <ModalBody p={4}>
            {selectedRecording?.recordingUrl ? (
              <Box borderRadius="xl" overflow="hidden" maxH="75vh">
                <VideoPlayer src={selectedRecording.recordingUrl} />
              </Box>
            ) : (
              <Flex justify="center" align="center" h="200px">
                <Text color="gray.400">لا يوجد ملف تسجيل متاح</Text>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(0.95); opacity: 0.8; }
            50% { transform: scale(1.1); opacity: 1; }
            100% { transform: scale(0.95); opacity: 0.8; }
          }
          .pulse-animation {
            animation: pulse 1.8s infinite ease-in-out;
          }
        `}
      </style>
    </Box>
  );
}



